import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { Alert, Button, Col, ControlLabel, Form, FormGroup, Label, Overlay, Panel, Popover } from 'react-bootstrap';
import ReactLoading from 'react-loading';
import EpletMmGraph from './eplet-mm-graph';
import { IRecipient } from '@nkr/models/recipient';
import useEpletGraphData from '@nkr/store/recipient/hooks/use-eplet-graph-data';
import styled from 'styled-components';
import { styles } from './styles';

const ContentWrapper = styled(Form)`
    // .form-group {
    //     margin-bottom: 5px;
    // }

    .form-group:last-child {
        margin-bottom: 0px;
    }

    .label {
        font-size: 90%;
    }

    .alert {
        font-size: 90%;
    }

    .pref-label {
        margin-right: 5px;
        font-weight: bold;
    }
`;

interface IProps {
    show: boolean,
    rowRef: any,
    tableRef: MutableRefObject<any>,
    recipient: IRecipient,
    onHide: () => void
}

const EpletMmGraphOverlay = (props: IProps) => {
    const { show, rowRef, tableRef } = props;

    const { data, error } = useEpletGraphData(show ? props.recipient?.id : null, { cache: true });

    const [recp, setRecipientData] = useState<IRecipient>({} as IRecipient);

    const popoverRef = useRef(null);

    const showingGraph = data != null;

    const shiftRef = useRef(0);
    const [shiftY, setShiftY] = useState(0);
    const dockRef = useRef(0);
    const [dockY, setDockY] = useState(0);


    useEffect(() => {
        setRecipientData(props.recipient);
    }, [props.recipient, showingGraph]);


    useEffect(() => {
        if (!show) {
            shiftRef.current = 0;
            setShiftY(0);
            dockRef.current = 0;
            setDockY(0);
            return;
        }

        const GUTTER = 12;

        const fit = () => {
            const el = document.getElementById('eplet-overlay');
            if (!el) return;

            const naturalTop = el.getBoundingClientRect().top - shiftRef.current;
            const overflow = GUTTER - naturalTop;

            const apply = (px: number) => {
                shiftRef.current = px;
                setShiftY(px);
            };

            if (overflow <= 0) {
                apply(0);
                return;
            }

            const scrollBy = Math.min(overflow, window.scrollY);
            if (scrollBy > 0) window.scrollBy(0, -scrollBy);

            apply(Math.max(0, overflow - scrollBy));
        };

        const raf = requestAnimationFrame(fit);
        window.addEventListener('resize', fit);

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', fit);
        };
    }, [show, showingGraph, dockY]);


    useEffect(() => {
        if (!show) return;

        const el = document.getElementById('eplet-overlay');
        if (!el) return;

        const DETACH_MAX = 24;

        const aim = () => {
            const arrow = el.querySelector('.arrow') as HTMLElement | null;
            const target: any = rowRef && 'current' in rowRef ? rowRef.current : rowRef;
            if (!arrow || typeof target?.getBoundingClientRect !== 'function') return;

            arrow.style.transform = '';           // measure clean
            const a = arrow.getBoundingClientRect();
            const t = target.getBoundingClientRect();

            const dx = (t.left + t.width / 2) - (a.left + a.width / 2);
            const dy = t.top - a.bottom;
            const need = dy + shiftRef.current;   // alignment absent the clipping push

            if (Math.abs(need) > 0.5) {
                dockRef.current += need;
                setDockY(dockRef.current);
            }

            arrow.style.transform = `translate(${dx}px, ${-shiftRef.current}px)`;
            arrow.style.visibility =
                shiftRef.current > DETACH_MAX ? 'hidden' : 'visible';
        };

        const raf = requestAnimationFrame(aim);
        const ro = new ResizeObserver(aim);
        ro.observe(el);

        return () => { cancelAnimationFrame(raf); ro.disconnect(); };
    }, [show, showingGraph, shiftY, dockY, rowRef]);


    return (
        <Overlay
            show={show}
            placement='top'
            target={rowRef}
            container={tableRef.current}
        >
            <Popover
                ref={popoverRef}
                id='eplet-overlay'
                style={{
                    maxWidth: '1200px',
                    minWidth: '1200px',
                    minHeight: '400px',
                    marginTop: shiftY + dockY
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'right', alignItems: 'right' }}>
                    <span style={{ cursor: 'pointer' }} className={'glyphicon glyphicon-remove'} aria-hidden='true' onClick={() => props.onHide()} />&nbsp;
                </div>

                {showingGraph ? (
                    <div>
                        <EpletMmGraph graphData={data} recipient={props.recipient} />
                        <Panel>
                            <Panel.Heading style={{ backgroundColor: 'white' }}>Donor Preferences</Panel.Heading>
                            <Panel.Body>
                                <ContentWrapper>
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 15 }}>
                                        {recp.histogeneticsRawTypingFile.length > 5 && (
                                            <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                                                <div className='pref-label'>Eplet MM:</div>
                                                <div>
                                                    <Label bsStyle='success' bsSize='lg'>
                                                        {{
                                                            '-1': 'Unknown',
                                                            '0': 'Any',
                                                            '2': 'Low',
                                                        }[recp.requireEpletId?.toString()] || 'Unknown'}
                                                    </Label>
                                                </div>
                                            </div>
                                        )}

                                        <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                                            <div className='pref-label'>CMV:</div>
                                            <div>
                                                <Label bsStyle='success' bsSize='lg'>
                                                    {{
                                                        '0': 'Unknown',
                                                        '1': 'Any',
                                                        '2': 'Positive',
                                                        '3': 'Negative',
                                                    }[recp.cmvPreferenceId?.toString()] || 'Unknown'}
                                                </Label>
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <div className='pref-label'>EBV:</div>
                                            <div>
                                                <Label bsStyle='success' bsSize='lg'>
                                                    {{
                                                        '0': 'Unknown',
                                                        '1': 'Any',
                                                        '2': 'Positive',
                                                        '3': 'Negative',
                                                    }[recp.ebvPreferenceId?.toString()] || 'Unknown'}
                                                </Label>
                                            </div>
                                        </div>
                                    </div>

                                    <FormGroup>
                                        <ControlLabel>Screening Comments:</ControlLabel>
                                        <Alert bsSize='sm' style={{ backgroundColor: '#f7f7f7', borderColor: '#f7f7f7', marginBottom: 0 }}>
                                            <div>
                                                {recp.screeningComments || <em>No comments provided.</em>}
                                            </div>
                                        </Alert>
                                    </FormGroup>
                                </ContentWrapper>
                            </Panel.Body>
                        </Panel>
                    </div>)
                    : error ?
                    <div style={styles.message}>{error}</div>
                    :
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '5px', marginTop: '145px' }}>
                        <ReactLoading type={'spin'} color={'black'} />
                    </div>
                }

                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    height: 'auto',
                    padding: '0',
                    margin: '0',
                    overflow: 'hidden'
                }}>
                    <div style={{ flex: '1', display: 'flex', justifyContent: 'center', marginLeft: '6vw', marginTop: showingGraph ? '' : '186px' }}>
                        <Button style={{ margin: '0', padding: '5px 10px' }} onClick={() => props.onHide()}>
                            Dismiss
                        </Button>
                    </div>

                    <div style={{ flex: '0 1 auto', textAlign: 'right', marginTop: showingGraph ? '' : '186px', marginRight: '1vw' }}>
                        <a href={'/center/' + props.recipient?.center.id + '/recipient/' + props.recipient?.id + '/profile/activity-log'} target='_blank'>
                            Go To Recipient Activity Log
                        </a>
                    </div>

                    <div style={{ flex: '0 1 auto', textAlign: 'right', marginTop: showingGraph ? '' : '186px' }}>
                        <a href={'/center/' + props.recipient?.center.id + '/recipient/' + props.recipient?.id + '/profile/donor-declines?ddrtype=2'} target='_blank'>
                            Go To Recipient DDRH
                        </a>
                    </div>
                </div>
            </Popover>
        </Overlay>

    )
}

export default EpletMmGraphOverlay;


